import { defineMessages } from "react-intl";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";

const title = defineMessages({
  step1: {
    id: "guides.mapOnboarding.groups.step1",
    defaultMessage: "Groups 🗂",
  },
  step2: {
    id: "guides.mapOnboarding.groups.step2",
    defaultMessage: "Manage Groups",
  },
  step3: {
    id: "guides.mapOnboarding.groups.step3",
    defaultMessage: "Group Creation",
  },
  step4: {
    id: "guides.mapOnboarding.groups.step4",
    defaultMessage: "First, fill out some details about your Group",
  },
  step5: {
    id: "guides.mapOnboarding.groups.step5",
    defaultMessage: "Create your Group",
  },
  step6: {
    id: "guides.mapOnboarding.groups.step6",
    defaultMessage: "Select groups using the Lasso",
  },
  step7: {
    id: "guides.mapOnboarding.groups.step7",
    defaultMessage: "Now, add these pins to the group!",
  },
  step8: {
    id: "guides.mapOnboarding.groups.step8",
    defaultMessage: "Nice job, you have added records to a group",
  },
});

const description = defineMessages({
  step1: {
    id: "guides.mapOnboarding.groups.step1.description",
    defaultMessage:
      "Use groups to organize your records by customer tier, product line, and more! \nClick the Groups Icon to get started.",
  },
  step2: {
    id: "guides.mapOnboarding.groups.step2.description",
    defaultMessage:
      "Here, you can:{br}" +
      "🔎 Search for groups{br}" +
      "✍️ Edit Groups{br}" +
      "🗺 View Groups on the Map",
  },
  step3: {
    id: "guides.mapOnboarding.groups.step3.description",
    defaultMessage: "Let's get started by creating a Group.",
  },
  step4: {
    id: "guides.mapOnboarding.groups.step4.description",
    defaultMessage:
      "1️⃣ Give your Group a name and color{br}" +
      "2️⃣ Assign records in this group to a frequency (a frequency will remind you to follow up with customers at regular intervals){br}" +
      "3️⃣ Share the records in the group instantly{br}" +
      '4️⃣ Click "Create" (Don\'t worry, you can delete this group later)',
  },
  step5: {
    id: "guides.mapOnboarding.groups.step5.description",
    defaultMessage: "Select this option to assign records to this Group",
  },
  step6: {
    id: "guides.mapOnboarding.groups.step6.description",
    defaultMessage:
      "⭕️ You can lasso multiple areas at once to choose companies{br}" +
      "↩️ Hit the undo button if you make a mistake{br}" +
      "Once you are done, click Add to Group!",
  },
  step7: {
    id: "guides.mapOnboarding.groups.step7.description",
    defaultMessage: "Click 'add to group' button",
  },
  step8: {
    id: "guides.mapOnboarding.groups.step8.description",
    defaultMessage: "🗄 You can search or filter through your records here",
  },
});

export const groupsTour: TourDescriptor = {
  id: "map-onboarding-groups",
  steps: [
    {
      id: "groups-button",
      description: description.step1,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      selector:
        "div[class*=MapPage_container] div[class*=Toolbar_container] [data-menu-item=groups]",
      title: title.step1,
      waitForEvent: {
        eventType: "click",
        selector:
          "div[class*=MapPage_container] div[class*=Toolbar_container] [data-menu-item=groups]",
      },
    },
    {
      id: "groups-modal",
      description: description.step2,
      selector: "[class*=GroupsModal_modal]",
      title: title.step2,
    },
    {
      id: "groups-modal-add",
      description: description.step3,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      selector: "[class*=GroupsModal_modal] [data-id=add-or-create-button]",
      title: title.step3,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=GroupsModal_modal] [data-id=add-or-create-button]",
      },
    },
    {
      id: "create-groups-modal",
      description: description.step4,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      selector: "[class*=CreateGroupModal_modal]",
      title: title.step4,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=CreateGroupModal_modal] [class*=Footer] .mmc-button--primary",
      },
    },
    {
      id: "create-groups-modal-dropdown",
      description: description.step5,
      options: {
        disableInteraction: false,
        expectRouteChange: true,
        showButtons: false,
      },
      selector: ".js-mmc-create-group-create-and-select",
      title: title.step5,
      waitForEvent: {
        eventType: "click",
        selector: ".js-mmc-create-group-create-and-select",
      },
    },
    {
      id: "create-groups-select-pins",
      description: description.step6,
      onBefore: async () => {
        return new Promise((resolve) => {
          waitForElement("[class*=LassoToolPanel_container]", resolve);
        });
      },
      title: title.step6,
    },
    {
      id: "create-groups-add-records",
      description: description.step7,
      options: {
        disableInteraction: false,
        expectRouteChange: true,
        showButtons: false,
      },
      selector: "div[class*=LassoToolPanel_tools] button",
      title: title.step7,
      waitForEvent: {
        eventType: "click",
        selector: "div[class*=LassoToolPanel_tools] button",
      },
    },
    {
      id: "create-groups-success",
      description: description.step8,
      onBefore: async () => {
        return new Promise((resolve) => {
          waitForElement("[class*=GroupsPage_container]", resolve);
        });
      },
      title: title.step8,
    },
  ],
};
